/**
 * SammanhangsCard — shows area context for a place in the editor sidebar.
 * Read-only for ambassadors, editable for admin (links to area editor).
 */
import { Link } from "react-router-dom";
import { MapPin, ChevronRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { usePlaceAreas, type PlaceAreaLink } from "@/hooks/useAdminAreas";

const TYPE_LABELS: Record<string, string> = {
  bygd: "Bygd",
  destination: "Destination",
  landscape_cluster: "Landskapskluster",
  region: "Region",
};

interface SammanhangsCardProps {
  placeId: string;
  /** If true, show edit links (admin). If false, read-only (ambassador). */
  isAdmin?: boolean;
}

export function SammanhangsCard({ placeId, isAdmin = true }: SammanhangsCardProps) {
  const { data: areas, isLoading } = usePlaceAreas(placeId);

  if (isLoading) {
    return (
      <div className="rounded-lg border bg-card text-card-foreground">
        <div className="px-4 py-3 border-b">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Sammanhang
          </h3>
        </div>
        <div className="p-4">
          <Skeleton className="h-8 w-full" />
        </div>
      </div>
    );
  }

  const primary = areas?.find((a) => a.is_primary);
  const others = areas?.filter((a) => !a.is_primary) || [];

  return (
    <div className="rounded-lg border bg-card text-card-foreground">
      <div className="px-4 py-3 border-b">
        <div className="flex items-center gap-2">
          <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Sammanhang
          </h3>
        </div>
        {primary && (
          <p className="text-[11px] text-muted-foreground mt-1">
            Del av <span className="font-medium text-foreground/80">{primary.area_name}</span>
          </p>
        )}
      </div>

      {(!areas || areas.length === 0) ? (
        <div className="px-4 py-4">
          <p className="text-xs text-muted-foreground/70">
            Inte kopplad till något område ännu.
          </p>
          {isAdmin && (
            <p className="text-[11px] text-muted-foreground/50 mt-1">
              Koppla via områdesredigeraren.
            </p>
          )}
        </div>
      ) : (
        <div className="divide-y">
          {[...(primary ? [primary] : []), ...others].map((area) => {
            const inner = (
              <div className="flex items-center gap-2.5 px-4 py-2.5">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-medium text-foreground truncate">
                      {area.area_name}
                    </span>
                    {area.is_primary && (
                      <Badge variant="secondary" className="text-[9px] py-0 px-1">
                        Primär
                      </Badge>
                    )}
                  </div>
                  <span className="text-[11px] text-muted-foreground">
                    {TYPE_LABELS[area.area_type] || area.area_type}
                  </span>
                </div>
                {isAdmin && (
                  <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                )}
              </div>
            );

            return isAdmin ? (
              <Link
                key={area.area_id}
                to={`/app/admin/areas/${area.area_id}/edit`}
                className="block hover:bg-muted/30 transition-colors"
              >
                {inner}
              </Link>
            ) : (
              <div key={area.area_id}>{inner}</div>
            );
          })}
        </div>
      )}
    </div>
  );
}
